import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AppHotelModel } from '../views/models/app-hotel.model';
import { Printer, PrinterSetup } from '../views/models/printer.model';
import { RequestHeaderModel } from '../views/models/request-header.model';
import * as EscPosEncoder from 'esc-pos-encoder';
import * as dayjs from 'dayjs';
import { AnalysisData } from '../views/models/analysis.model';

@Injectable({
  providedIn: 'root'
})
export class PrinterService {

  setups: PrinterSetup[] = [];
  printers: Printer[] = [];
  localPrinterMachines: { hostname: string, printers: string[] } = null;
  localPrinters: Printer[] = [];

  localSetup = false;

  constructor(
    private http: HttpClient
  ) { }

  getSetups(): Promise<PrinterSetup[]> {
    return new Promise((resolve, reject) => {
      this.http.get(environment.baseURL + '/v2/api/printer_selections').subscribe({
        next: (resp: PrinterSetup[]) => {
          this.setups = resp;
          resolve(resp);
        },
        error: err => {
          reject(err);
        }
      });
    });
  }

  getPrinters(): Promise<Printer[]> {
    return new Promise((resolve, reject) => {
      this.http.get(environment.baseURL + '/v2/api/printers').subscribe({
        next: (resp: Printer[]) => {
          this.printers = resp;
          resolve(resp);
        },
        error: err => {
          reject(err);
        }
      });
    });
  }

  postSetup(data: PrinterSetup): Promise<PrinterSetup> {
    return new Promise((resolve, reject) => {
      this.http.post(environment.baseURL + '/v2/api/printer_selection', data).subscribe({
        next: async (resp: PrinterSetup) => {
          await this.getSetups();
          resolve(resp);
        },
        error: err => {
          reject(err);
        }
      });
    });
  }

  deleteSetup(setupId: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.delete(environment.baseURL + '/v2/api/printer_selection/' + setupId).subscribe({
        next: async () => {
          await this.getSetups();
          resolve(true);
        },
        error: err => {
          reject(err);
        }
      });
    });
  }

  getLocalPrinters() {
    return new Promise((resolve, reject) => {
      this.http.get('http://localhost:4051/printers').subscribe({
        next: async (data: { hostname: string, printers: string[] }) => {
          this.localSetup = true;
          this.localPrinterMachines = data;
          resolve(true);
        },
        error: err => {
          this.localSetup = false;
          reject(err);
        }
      });
    });
  }

  getLocalPrinterQues() {
    return new Promise((resolve, reject) => {
      this.http.get('http://localhost:4051/printer_ques').subscribe({
        next: async (data: Printer[]) => {
          this.localSetup = true;
          this.localPrinters = data;
          resolve(true);
        },
        error: err => {
          this.localSetup = false;
          reject(err);
        }
      });
    });
  }

  postPrinter(data: Printer) {
    return new Promise((resolve, reject) => {
      this.http.post(environment.baseURL + '/v2/api/printer', data).subscribe({
        next: async (resp: Printer) => {
          await this.postPrinterLocal(resp);
          resolve(resp);
        },
        error: err => {
          reject(err);
        }
      });
    });
  }

  deletePrinter(printerId: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.delete(environment.baseURL + '/v2/api/printer/' + printerId).subscribe({
        next: async () => {
          await this.getPrinters();
          await this.deletePrinterLocal(printerId);
          resolve(true);
        },
        error: err => {
          reject(err);
        }
      });
    });
  }

  postPrinterLocal(data: Printer) {
    return new Promise((resolve, reject) => {
      this.http.post('http://localhost:4051/printer_que', data).subscribe({
        next: async (resp: Printer) => {
          resolve(resp);
        },
        error: err => {
          reject(err);
        }
      });
    });
  }

  deletePrinterLocal(printerId: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.delete('http://localhost:4051/printer_que/' + printerId).subscribe({
        next: async () => {
          await this.getPrinters();
          resolve(true);
        },
        error: err => {
          reject(err);
        }
      });
    });
  }

  postReceipt(data: { content: string; printer_id: number }): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.post(environment.baseURL + '/v2/api/assign_print_esc_pos', data).subscribe({
        next: () => {
          resolve(true);
        },
        error: err => {
          reject(err);
        }
      });
    });
  }

  alignRightnLeft(left: string, right: string, size: number) {
    left = left.length > 35 ? left.slice(0, 32) + '...' : left;
    const spaces = size - right.length - left.length;

    let res = left;

    for (let i = 0; i < spaces; i++) {
      res += ' ';
    }
    res += right;

    return res;
  }

  turkishChar(value: string) {
    return value
      .replace(/Ğ/g, 'G').replace(/ğ/g, 'g')
      .replace(/Ü/g, 'U').replace(/ü/g, 'u')
      .replace(/Ş/g, 'S').replace(/ş/g, 's')
      .replace(/İ/g, 'I').replace(/ı/g, 'i')
      .replace(/Ö/g, 'O').replace(/ö/g, 'o')
      .replace(/Ç/g, 'C').replace(/ç/g, 'c');
  }

  wordWithLines(word: string, length: number) {
    const COUNT = Math.round((length - word.length) / 2);

    let result = '';
    for (let i = 0; i < COUNT; i++) {
      result += '-';
    }

    result += word;

    for (let i = 0; i < COUNT; i++) {
      result += '-';
    }
  }

  kitchen(rData: AppHotelModel, request: RequestHeaderModel, printerId: number, alias: string): Promise<boolean> {
    return new Promise(async (resolve, reject) => {

      const MM72 = alias.includes('72mm');


      const BOOKPAX = localStorage.getItem('peopleCount')
        || (request.type === 'reservation_to_facility' ? request.requestlines[0].quantity : 1);

      const reservationType = request.reservation_type === 'table' ? 'Table Service' :
        request.reservation_type === 'order' ? 'Delivery' : request.reservation_type === 'come' ? 'Pickup' : 'Drive Thru';

      const NAME = rData.name.length > (MM72 ? 21 : 24)
        // eslint-disable-next-line max-len
        ? `${rData.name.split(' ')[0]} ${rData.name.split(' ')[1]}\n${rData.name.replace(`${rData.name.split(' ')[0]} ${rData.name.split(' ')[1]}`, '')}` :
        rData.name;

      let encoder = new EscPosEncoder();

      encoder.initialize()
        .size('large')
        .align('center')
        .line(this.wordWithLines(reservationType.toUpperCase(), (MM72 ? 21 : 24)))
        .line((MM72 ? '---------------------' : '------------------------'))
        .line(NAME)
        .line((MM72 ? '---------------------' : '------------------------'))
        .line(request.payment_type)
        .line('Order No:' + request.id)
        .line((MM72 ? '---------------------' : '------------------------'))
        .size('medium')
        .align('left')
        // eslint-disable-next-line max-len
        .line(this.alignRightnLeft(`Date: ${dayjs(new Date()).format('DD/MM/YYYY')}`, `Time: ${dayjs(new Date()).format('HH:mm')}`, (MM72 ? 42 : 48)))
        .bold()
        // eslint-disable-next-line max-len
        .line(`${request.room_no.includes('Table ') ? '' : `Order Date: ${dayjs(request.order_date).format('DD/MM/YYYY')}\nCollection Time: ${dayjs(request.order_date).format('HH:mm')}`}`)
        .size('large')
        .line((MM72 ? '---------------------' : '------------------------'))
        .size('medium')
        .line(`Customer: ${this.turkishChar(request.profile_name)}`)
        .line(request.phone_no ? `Phone: ${request.phone_no}` : '')
        .line(`${request.reservation_type === 'table'
          // eslint-disable-next-line max-len
          ? `Table: ${request.room_no}`
          : request.reservation_type === 'order' ? `Address: ${request.room_no}` : ''}\n\n`)
        .align('left');

      request.requestlines.forEach(line => {
        encoder = encoder.line(`${line.quantity} ${line.unit_of_measure} x ${line.name}`);
        if (line.note) {
          encoder = encoder.line(`${line.note ? `Notes: ${this.turkishChar(line.note)}` : null}`);
        }
      });

      if (request.note) {
        encoder = encoder.line(`${request.note ? `\nNotes: ${this.turkishChar(request.note)}` : null}`);
      }

      const RESULT = encoder.line('\n\n\n\n\n\n')
        .cut('partial')
        .encode();

      let BYTE = '';

      const LEGNTH = RESULT.byteLength;

      for (let i = 0; i < LEGNTH; i++) {
        BYTE += String.fromCharCode(RESULT[i]);
      }

      const base64 = btoa(BYTE);

      const DATA = {
        content: base64,
        printer_id: printerId
      };

      try {
        await this.postReceipt(DATA);
        resolve(true);
      } catch (err) {
        reject(err);
      }
    });
  }

  check(rData: AppHotelModel, request: RequestHeaderModel, printerId: number, alias: string): Promise<boolean> {
    return new Promise(async (resolve, reject) => {

      const MM72 = alias.includes('72mm');

      const NAME = rData.name.length > (MM72 ? 21 : 24)
        // eslint-disable-next-line max-len
        ? `${rData.name.split(' ')[0]} ${rData.name.split(' ')[1]}\n${rData.name.replace(`${rData.name.split(' ')[0]} ${rData.name.split(' ')[1]}`, '')}` :
        rData.name;

      const reservationType = request.reservation_type === 'table' ? 'Table Service' :
        request.reservation_type === 'order' ? 'Delivery' : request.reservation_type === 'come' ? 'Pickup' : 'Drive Thru';

      let encoder = new EscPosEncoder();

      encoder.initialize()
        .size('large')
        .align('center')
        .line(this.wordWithLines(reservationType.toUpperCase(), (MM72 ? 21 : 24)))
        .line((MM72 ? '---------------------' : '------------------------'))
        .line(NAME)
        .line((MM72 ? '---------------------' : '------------------------'))
        .line(request.payment_type)
        .line('Order No:' + request.id)
        .line((MM72 ? '---------------------' : '------------------------'))
        .size('medium')
        .align('left')
        // eslint-disable-next-line max-len
        .line(this.alignRightnLeft(`Date: ${dayjs(new Date()).format('DD/MM/YYYY')}`, `Time: ${dayjs(new Date()).format('HH:mm')}`, (MM72 ? 42 : 48)))
        .bold()
        // eslint-disable-next-line max-len
        .line(`${request.room_no.includes('Table ') ? '' : `Order Date: ${dayjs(request.order_date).format('DD/MM/YYYY')}\nCollection Time: ${dayjs(request.order_date).format('HH:mm')}`}`)
        .size('large')
        .line((MM72 ? '---------------------' : '------------------------'))
        .size('medium')
        .line(`Customer: ${this.turkishChar(request.profile_name)}`)
        .line(request.phone_no ? `Phone: ${request.phone_no}` : '')
        .line(`${request.reservation_type === 'table'
          // eslint-disable-next-line max-len
          ? `Table: ${request.room_no}`
          : request.reservation_type === 'order' ? `Address: ${request.room_no}` : ''}\n\n`)
        .align('left');

      let total = 0;

      request.requestlines.forEach(line => {
        // eslint-disable-next-line max-len
        encoder = encoder.line(this.alignRightnLeft(`${line.quantity} ${line.unit_of_measure} x ${this.turkishChar(line.name)}`, `${(Math.round(line.amount * 100) / 100).toFixed(2)}`, (MM72 ? 42 : 48)));
        total += line.amount;
      });

      encoder = encoder
        .line((MM72 ? '---------------------' : '------------------------'))
        .size('large')
        .line(this.alignRightnLeft('TOTAL', `${(Math.round(total * 100) / 100).toFixed(2)}`, (MM72 ? 21 : 24)));

      if (request.note) {
        encoder = encoder.line(`${request.note ? `\nNotes: ${this.turkishChar(request.note)}` : null}`)
          .align('center').size('large')
          .line('THANK YOU');
      }

      const RESULT = encoder.line('\n\n\n\n\n\n')
        .cut('partial')
        .encode();

      let BYTE = '';

      const LENGTH = RESULT.byteLength;

      for (let i = 0; i < LENGTH; i++) {
        BYTE += String.fromCharCode(RESULT[i]);
      }

      const base64 = btoa(BYTE);

      const DATA = {
        content: base64,
        printer_id: printerId
      };

      try {
        await this.postReceipt(DATA);
        resolve(true);
      } catch (err) {
        reject(err);
      }
    });
  }

  endOfDayReport(
    analysis: AnalysisData[],
    paymentSales: {
      'cash': number,
      'card': number
    },
    printerId: number): Promise<boolean> {
    return new Promise(async (resolve, reject) => {

      const encoder = new EscPosEncoder();

      encoder.initialize()
        .size('large')
        .align('center')
        .line('END OF DAY REPORT')
        .line('------------------------')
        .line('Date: ' + dayjs(new Date()).format('DD/MM/YYYY'))
        .line('Time: ' + dayjs(new Date()).format('HH:mm'))
        .line('------------------------')
        .size('medium')
        .align('left')
        .line('Total Orders: ' + analysis.length)
        .line('Total Amount: ' + analysis.reduce((a, b) => a + b.invoice_amount, 0))
        .line('Total Discount: ' + analysis.reduce((a, b) => a + b.invoice_discount_amount, 0))
        .line('------------------------')
        .line('Cash Sales: ' + paymentSales.cash)
        .line('Card Sales: ' + paymentSales.card)
        .line('------------------------');


      const RESULT = encoder.line('\n\n\n\n\n\n')
        .cut('partial')
        .encode();

      let BYTE = '';

      const LENGTH = RESULT.byteLength;

      for (let i = 0; i < LENGTH; i++) {
        BYTE += String.fromCharCode(RESULT[i]);
      }

      const base64 = btoa(BYTE);

      const DATA = {
        content: base64,
        printer_id: printerId
      };

      try {
        await this.postReceipt(DATA);
        resolve(true);
      } catch (err) {
        reject(err);
      }
    });
  }
}
